import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { userInitials } from '../settings/userDisplay';

export type UserAvatarSize = 'sm' | 'md' | 'lg';

export interface UserAvatarProps {
  username: string | null;
  displayName?: string | null;
  /** When false, skip loading the photo URL (initials only). When null/undefined, attempt photo load. */
  hasPhoto?: boolean | null;
  photoUrl?: string;
  /** When true, never attempt to load a profile photo (initials only). */
  initialsOnly?: boolean;
  /** Custom avatar fill; skips the default brand gradient. */
  accentColor?: string;
  size?: UserAvatarSize;
  className?: string;
}

const SIZE_CLASS: Record<UserAvatarSize, string> = {
  sm: 'user-avatar--sm',
  md: 'user-avatar--md',
  lg: 'user-avatar--lg'
};

export function UserAvatar({
  username,
  displayName,
  hasPhoto,
  photoUrl = '/api/auth/profile/photo',
  initialsOnly = false,
  accentColor,
  size = 'md',
  className
}: UserAvatarProps) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const initials = userInitials(displayName ?? username);
  const showPhoto = !initialsOnly && hasPhoto !== false && !photoFailed;
  const label = displayName?.trim() || username?.trim() || 'User';
  const accentStyle: CSSProperties | undefined = accentColor
    ? { background: accentColor, ['--avatar-accent' as string]: accentColor }
    : undefined;

  useEffect(() => {
    setPhotoFailed(false);
    setPhotoLoaded(false);
  }, [photoUrl, hasPhoto]);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setPhotoLoaded(true);
    }
  }, [photoUrl, hasPhoto, showPhoto]);

  const classes = [
    'user-avatar',
    SIZE_CLASS[size],
    accentColor ? 'user-avatar--colored' : null,
    className
  ]
    .filter(Boolean)
    .join(' ');

  if (showPhoto) {
    return (
      <span className={classes} aria-hidden>
        {!photoLoaded ? (
          <span className="user-avatar__initials">{initials}</span>
        ) : null}
        <img
          ref={imgRef}
          className="user-avatar__img"
          src={photoUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setPhotoLoaded(true)}
          onError={() => setPhotoFailed(true)}
        />
      </span>
    );
  }

  return (
    <span className={classes} style={accentStyle} aria-label={label}>
      {initials}
    </span>
  );
}
